import cx from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useKeenSlider } from 'keen-slider/react'
import { ReactNode, useContext, useState } from 'react'

import { flipAnimation } from '@lib/animate'
import { StringsContext } from '@lib/strings-context'

import Icon from '@components/icon'

interface PhotoCarouselProps {
  slideCount: number
  hasArrows?: boolean
  hasDots?: boolean
  hasCounter?: boolean
  hasDrag?: boolean
  className?: string
  children: ReactNode
}

const PhotoCarousel = ({
  slideCount,
  hasArrows,
  hasDots,
  hasCounter,
  hasDrag = true,
  className,
  children,
}: PhotoCarouselProps) => {
  const strings = useContext(StringsContext)

  const indices = [...Array(slideCount).keys()]

  const [currentSlide, setCurrentSlide] = useState(0)
  const [sliderRef, sliderInstanceRef] = useKeenSlider<HTMLDivElement>({
    selector: '.carousel-slide',
    slides: slideCount,
    loop: true,
    defaultAnimation: {
      duration: 800,
    },
    dragSpeed: 0.8,
    drag: hasDrag,
    slideChanged: (slider) => {
      const newIndex = slider.track.details.rel
      setCurrentSlide(newIndex)
    },
  })

  return (
    <div
      className={cx(
        'relative flex flex-col gap-y-2.5 w-full bg-transparent',
        className,
      )}
    >
      <div
        ref={sliderRef}
        className={cx(
          'keen-slider',
          'flex relative overflow-hidden will-change-transform touch-action touch-action-pan-y items-center',
          {
            'cursor-grab active:cursor-grabbing': hasDrag,
          },
        )}
      >
        {children}
      </div>

      {slideCount > 1 && (
        <div className="flex md:absolute md:bottom-0 md:inset-x-0 transform pointer-events-none justify-start translate-y-0">
          <div className="flex justify-between items-center w-full md:px-5 md:py-6 rounded-full pointer-events-auto static bg-transparent border-none">
            <div className="flex items-center justify-center relative">
              {hasDots && (
                <div className="relative flex items-center">
                  {indices.map((index) => (
                    <button
                      key={index}
                      aria-label={strings.carouselDotLabel.replace(
                        /{index}/gi,
                        (index + 1).toString(),
                      )}
                      onClick={() => {
                        sliderInstanceRef.current?.moveToIdx(index)
                      }}
                      className={cx(
                        'p-1 bg-transparent',
                        'before:block before:relative before:w-2 before:h-2 before:bg-white before:rounded-full before:opacity-30',
                        'before:transition-carousel-dots',
                        'hover:before:opacity-100',
                        {
                          'before:opacity-100 transform scale-150':
                            currentSlide === index,
                        },
                      )}
                    />
                  ))}
                </div>
              )}

              {hasCounter && (
                <div className="flex gap-x-1 text-base font-normal text-black select-none">
                  <div className="relative w-2 overflow-hidden">
                    {/*
                      Note: when using framer-motion version 9.0.3 or above this sometimes shows 2 numbers on top of each other when moving swiftly.
                      Downgrading framer-motion to version 9.0.2 until this bug is fixed: https://github.com/framer/motion/issues/2023
                      */}
                    <AnimatePresence initial={false}>
                      <motion.span
                        key={currentSlide + 1}
                        initial="hide"
                        animate="show"
                        exit="hide"
                        variants={flipAnimation}
                        className="block will-change-transform absolute inset-x-0 top-0 last:static last:inset-x-auto last:top-auto"
                      >
                        {currentSlide + 1}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                  <div>{strings.carouselCounterText}</div>
                  <div>{slideCount}</div>
                </div>
              )}
            </div>

            {hasArrows && (
              <div className="flex gap-x-4">
                <button
                  aria-label={strings.carouselLeftArrowLabel}
                  onClick={() => {
                    sliderInstanceRef.current?.prev()
                  }}
                  className="rounded-full text-[22px] bg-transparent transition-colors duration-300"
                >
                  <Icon name="ChevronLeft" className="block" />
                </button>
                <button
                  aria-label={strings.carouselRightArrowLabel}
                  onClick={() => {
                    sliderInstanceRef.current?.next()
                  }}
                  className="rounded-full text-[22px] bg-transparent transition-colors duration-300"
                >
                  <Icon name="ChevronRight" className="block" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PhotoCarousel
